import React, { useState, useRef, useEffect } from "react";
import { useContactDrawer } from "../context/ContactDrawerContext";

export default function ContactDrawer() {
  const { isOpen, closeDrawer } = useContactDrawer();
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Client-side file validation constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 10;
  const MAX_TOTAL_SIZE_MB = 20;
  const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp"];

  // Close drawer on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeDrawer();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeDrawer]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.value ? e.target.files : []);
    if (files.length === 0) return;

    // Validate file count
    if (files.length > MAX_FILES) {
      alert(`Du kan højst uploade ${MAX_FILES} filer.`);
      e.target.value = "";
      setSelectedFiles([]);
      return;
    }

    // Validate individual file size and type
    let totalSize = 0;
    for (let file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Filtypen for "${file.name}" er ikke tilladt. Kun PDF, PNG, JPEG, GIF og WEBP er tilladt.`);
        e.target.value = "";
        setSelectedFiles([]);
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        alert(`Filen "${file.name}" er for stor. Hver fil må højst fylde ${MAX_FILE_SIZE_MB} MB.`);
        e.target.value = "";
        setSelectedFiles([]);
        return;
      }
      totalSize += file.size;
    }

    // Validate total size
    if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
      alert(`Den samlede størrelse af filerne overstiger grænsen på ${MAX_TOTAL_SIZE_MB} MB.`);
      e.target.value = "";
      setSelectedFiles([]);
      return;
    }

    setSelectedFiles(files);
  };

  const handleRemoveFile = () => {
    if (formRef.current) {
      const fileInput = formRef.current.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";
    }
    setSelectedFiles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    const form = formRef.current;
    const formData = new FormData(form);

    // Verify honeypot field is empty
    const honeypot = formData.get("website_url");
    if (honeypot) {
      // Silent fail for bots
      setIsSubmitting(false);
      setSubmitStatus("success");
      form.reset();
      setSelectedFiles([]);
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setSubmitStatus("success");
        form.reset();
        setSelectedFiles([]);
      } else {
        setSubmitStatus("error");
        setErrorMessage(result.message || "Der opstod en fejl under afsendelsen. Prøv venligst igen.");
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitStatus("error");
      setErrorMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        onClick={closeDrawer}
        className={`fixed inset-0 bg-black/60 backdrop-blur-xs z-50 transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Slide-over Panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full max-w-lg bg-brand-sand-50 shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out border-l border-brand-sand-200/60 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer Header */}
        <div className="p-6 border-b border-brand-sand-200 flex items-center justify-between bg-brand-green-950 text-white">
          <div className="flex flex-col gap-1">
            <span className="text-brand-green-400 font-semibold tracking-[0.15em] uppercase text-[10px]">
              Få et uforpligtende tilbud
            </span>
            <h3 className="font-sans text-xl font-bold tracking-tight text-brand-sand-50">
              Kontakt os i dag
            </h3>
          </div>
          <button
            onClick={closeDrawer}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors focus:outline-none cursor-pointer"
            aria-label="Luk menu"
          >
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        {/* Drawer Content (Scrollable) */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          {submitStatus === "success" ? (
            <div className="flex flex-col items-center text-center py-12 px-4 animate-in fade-in duration-300">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-3xl mb-6">
                <i className="fa-solid fa-circle-check" />
              </div>
              <h4 className="font-sans text-2xl font-bold text-brand-green-900 mb-3">
                Tak for din henvendelse!
              </h4>
              <p className="text-brand-charcoal/80 text-sm leading-relaxed mb-8 max-w-sm">
                Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.
              </p>
              <button
                onClick={() => {
                  setSubmitStatus(null);
                  closeDrawer();
                }}
                className="px-6 py-3 bg-brand-green-600 hover:bg-brand-green-500 text-white font-bold text-sm tracking-wide rounded-sm transition-all duration-300 cursor-pointer"
              >
                Luk vindue
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <p className="text-brand-charcoal/80 text-sm leading-relaxed">
                Udfyld formularen herunder med dine kontaktoplysninger og en kort beskrivelse af dit projekt, så vender vi tilbage med et uforpligtende tilbud.
              </p>

              {submitStatus === "error" && (
                <div className="p-4 bg-rose-50 border border-rose-200 text-rose-900 rounded-sm flex items-start gap-3 animate-in fade-in duration-200">
                  <i className="fa-solid fa-circle-exclamation text-rose-600 text-lg shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-sm">Der opstod en fejl</h4>
                    <p className="text-xs text-rose-800 mt-1">{errorMessage}</p>
                  </div>
                </div>
              )}

              <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Secure Antispam Honeypot Field */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url_drawer">Hjemmeside URL (Udfyld ikke denne)</label>
                  <input
                    type="text"
                    id="website_url_drawer"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Name Field */}
                <div className="flex flex-col gap-1.5 text-left">
                  <label htmlFor="drawer_name" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                    Navn <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="drawer_name"
                    name="name"
                    required
                    placeholder="Dit fulde navn"
                    className="w-full px-4 py-2.5 bg-white border border-brand-sand-300 rounded-sm focus:outline-none focus:border-brand-green-700 text-sm transition-colors"
                  />
                </div>

                {/* Phone Field */}
                <div className="flex flex-col gap-1.5 text-left">
                  <label htmlFor="drawer_phone" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                    Telefonnummer <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="drawer_phone"
                    name="phone"
                    required
                    placeholder="Dit telefonnummer"
                    className="w-full px-4 py-2.5 bg-white border border-brand-sand-300 rounded-sm focus:outline-none focus:border-brand-green-700 text-sm transition-colors"
                  />
                </div>

                {/* Email Field */}
                <div className="flex flex-col gap-1.5 text-left">
                  <label htmlFor="drawer_email" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                    E-mailadresse <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="drawer_email"
                    name="email"
                    required
                    placeholder="din.email@eksempel.dk"
                    className="w-full px-4 py-2.5 bg-white border border-brand-sand-300 rounded-sm focus:outline-none focus:border-brand-green-700 text-sm transition-colors"
                  />
                </div>

                {/* Message Field */}
                <div className="flex flex-col gap-1.5 text-left">
                  <label htmlFor="drawer_message" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                    Besked <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id="drawer_message"
                    name="message"
                    required
                    rows="4"
                    placeholder="Beskriv dit projekt, dine ønsker eller spørgsmål..."
                    className="w-full px-4 py-2.5 bg-white border border-brand-sand-300 rounded-sm focus:outline-none focus:border-brand-green-700 text-sm transition-colors resize-none"
                  />
                </div>

                {/* File Upload Field */}
                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                    Vedhæft filer <span className="text-brand-sand-500 font-normal lowercase">(valgfrit)</span>
                  </label>
                  
                  <div className="relative border border-dashed border-brand-sand-300 hover:border-brand-green-600 rounded-sm bg-white p-4 text-center transition-colors cursor-pointer group">
                    <input
                      type="file"
                      name="files"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center gap-2">
                      <i className="fa-solid fa-cloud-arrow-up text-brand-sand-400 group-hover:text-brand-green-600 text-2xl transition-colors" />
                      <span className="text-xs font-semibold text-brand-charcoal">
                        Klik for at vælge eller træk filer hertil
                      </span>
                      <span className="text-[10px] text-brand-sand-500">
                        PDF, PNG, JPG, GIF eller WEBP (Maks. 5 filer, i alt 20 MB)
                      </span>
                    </div>
                  </div>

                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-2 p-3 bg-brand-sand-100 rounded-sm border border-brand-sand-200 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 text-brand-charcoal/90 font-medium truncate">
                        <i className="fa-solid fa-paperclip text-brand-sand-500 shrink-0" />
                        <span className="truncate">
                          {selectedFiles.length === 1
                            ? selectedFiles[0].name
                            : `${selectedFiles.length} filer valgt`}
                        </span>
                        <span className="text-brand-sand-500 font-normal">
                          ({(selectedFiles.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="text-rose-600 hover:text-rose-800 font-semibold text-xs focus:outline-none cursor-pointer"
                      >
                        Fjern
                      </button>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 w-full py-3.5 bg-brand-green-600 hover:bg-brand-green-500 disabled:bg-brand-green-600/50 text-white font-bold text-xs tracking-wide rounded-sm transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin" />
                      <span>Sender...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane" />
                      <span>Send henvendelse</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-6 border-t border-brand-sand-200 bg-brand-sand-100 text-center text-xs text-brand-sand-600 flex flex-col gap-2">
          <div className="flex items-center justify-center gap-4 font-semibold text-brand-green-900">
            <a href="tel:+4520661517" className="hover:underline">
              <i className="fa-solid fa-phone mr-1" /> +45 20 66 15 17
            </a>
            <span>•</span>
            <a href="mailto:rasmus@elmegaardenhaveoganlaeg.dk" className="hover:underline">
              <i className="fa-solid fa-envelope mr-1" /> rasmus@elmegaardenhaveoganlaeg.dk
            </a>
          </div>
          <p>© 2026 Elmegaarden. Alle rettigheder forbeholdes.</p>
        </div>
      </div>
    </>
  );
}
